// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import API_BASE_URL from './config/api';

// ✅ Import all components
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Stats from './components/Stats';
import About from './components/About';
import Services from './components/Services';
import Gallery from './components/Gallery';
import Blog from './components/Blog';
import EventForm from './components/EventForm';
import Footer from './components/Footer';

// ✅ Import Auth components
import Login from './components/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';
import Admin from './pages/Admin';

const API_URL = `${API_BASE_URL}/api`;

// Main Home Component
function Home({ onCreateClick, showForm, setShowForm, events, eventName, setEventName, eventDate, setEventDate, eventLocation, setEventLocation, eventDescription, setEventDescription, eventType, setEventType, loading, addEvent }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-red-50">
      <Navbar onCreateClick={onCreateClick} />
      <Hero onCreateClick={onCreateClick} />
      <Stats />
      <About />
      <Services />
      <Gallery />
      <Blog />

      {showForm && (
        <EventForm
          eventName={eventName}
          setEventName={setEventName}
          eventDate={eventDate}
          setEventDate={setEventDate}
          eventLocation={eventLocation}
          setEventLocation={setEventLocation}
          eventDescription={eventDescription}
          setEventDescription={setEventDescription}
          eventType={eventType}
          setEventType={setEventType}
          loading={loading}
          onSubmit={addEvent}
          onClose={() => setShowForm(false)}
        />
      )}

      <Footer />
    </div>
  );
}

function App() {
  const [events, setEvents] = useState([]);
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventType, setEventType] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      console.log('📡 Fetching events from backend...');
      const res = await fetch(`${API_URL}/events`);
      const data = await res.json();
      setEvents(data);
      console.log('✅ Events fetched:', data.length, 'events');
    } catch (err) {
      console.error('❌ Error fetching events:', err);
    }
  };

  const addEvent = async () => {
    if (!eventName.trim() || !eventDate || !eventLocation.trim()) {
      alert('Please fill all required fields (Name, Date, Location)');
      return;
    }

    setLoading(true);
    console.log('📤 Creating new event...');
    
    try {
      const res = await fetch(`${API_URL}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: eventName,
          date: eventDate,
          location: eventLocation,
          description: eventDescription,
          type: eventType || 'Other',
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setEvents([...events, data]);
        
        setEventName('');
        setEventDate('');
        setEventLocation('');
        setEventDescription('');
        setEventType('');
        setShowForm(false);
        
        alert('✅ Event created successfully!');
        console.log('✅ Event created:', data);
      } else {
        const errorData = await res.json();
        alert('❌ Failed to create event: ' + (errorData.message || 'Unknown error'));
        console.error('❌ Server error:', errorData);
      }
    } catch (err) {
      console.error('❌ Error adding event:', err);
      alert('❌ Failed to create event. Please try again.');
    }
    setLoading(false);
  };

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Home Page */}
          <Route 
            path="/" 
            element={
              <Home 
                onCreateClick={() => setShowForm(true)}
                showForm={showForm}
                setShowForm={setShowForm}
                events={events}
                eventName={eventName}
                setEventName={setEventName}
                eventDate={eventDate}
                setEventDate={setEventDate}
                eventLocation={eventLocation}
                setEventLocation={setEventLocation}
                eventDescription={eventDescription}
                setEventDescription={setEventDescription}
                eventType={eventType}
                setEventType={setEventType}
                loading={loading}
                addEvent={addEvent}
              />
            } 
          />

          {/* Login Page */}
          <Route path="/login" element={<Login />} />

          {/* Register Page */}
          <Route path="/register" element={<Register />} />

          {/* Admin Page - Protected (Admin Only) */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute adminOnly={true}>
                <Admin />
              </ProtectedRoute>
            } 
          />

        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;/* Updated Wed, Dec 17, 2025  4:12:40 PM */
